import CognitoService from './CognitoService';

/**
 * Service for handling authentication operations
 */
class AuthService {
  /**
   * Process POST request authentication data
   * @param {Object} data - The POST request data containing token, user data, server data, and WebSocket connection info
   * @returns {Promise<Object>} - Authentication result with token, user data, server data, and WebSocket connection info
   */
  async processPostAuth(data) {
    try {
      const { token, user_data, server_data, ws_connection } = data;
      
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Validate token with Cognito
      const isValid = await CognitoService.validateToken(token);
      
      if (!isValid) {
        throw new Error('Invalid authentication token');
      }
      
      // CSRF token handling removed
      
      return {
        token,
        userData: user_data || {},
        serverData: server_data || [],
        wsConnection: ws_connection || null,
        // CSRF token field removed
      };
    } catch (error) {
      console.error('POST authentication error:', error);
      throw error;
    }
  }
  
  /**
   * Check if the current page load is from a POST request
   * @returns {boolean} - True if current load is from POST request
   */
  isPostRequestAuth() {
    // Check if there's POST data in sessionStorage
    return !!sessionStorage.getItem('postAuthData');
  }
  
  /**
   * Save POST authentication data to session storage
   * @param {Object} data - The authentication data to save
   */
  savePostAuthData(data) {
    // CSRF token handling removed
    sessionStorage.setItem('postAuthData', JSON.stringify(data));
  }
  
  /**
   * Get saved POST authentication data from session storage
   * @returns {Object|null} - The saved authentication data or null if not found
   */
  getPostAuthData() {
    const data = sessionStorage.getItem('postAuthData');
    return data ? JSON.parse(data) : null;
  }
  
  /**
   * Clear saved POST authentication data
   */
  clearPostAuthData() {
    sessionStorage.removeItem('postAuthData');
  }
  
  /**
   * Create authentication request headers
   * @returns {Object} - Headers object
   */
  createAuthHeaders() {
    // CSRF token header removed
    return {
      'Content-Type': 'application/json'
    };
  }
  
  /**
   * Prepare a request body
   * @param {Object} data - The request data
   * @returns {Object} - Request data (CSRF token handling removed)
   */
  prepareRequestBody(data = {}) {
    // CSRF token removed from request body
    return data;
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
